// Badge — the count / dot indicator (DESIGN-SYSTEM.md §7).
//
// One shape for "how many, and how bad": a tab handle's problem count, a nav
// row's advisory count, a diff tab's error count, a back-button's stack depth.
// Before this, each site hand-rolled its own `rounded-full` span and they
// disagreed on size (`min-w-[16px]` vs `min-w-5`), weight and colour source.
//
// Placement picks the visual weight, because the two uses need different ones:
//   corner — absolutely positioned over an icon; SOLID fill + a ring punched in
//            the surface behind it, so it stays legible on top of glyph strokes.
//   inline — sits in text flow; a SOFT tint, so it reads as an annotation
//            rather than an alarm.
import { cx } from "./classNames.js";

export type BadgeTone = "error" | "warning" | "todo" | "info" | "neutral";
export type BadgePlacement = "corner" | "inline";

const SOLID: Record<BadgeTone, string> = {
  error: "bg-red-400 text-slate-950",
  warning: "bg-amber-400 text-slate-950",
  todo: "bg-violet-300 text-slate-950",
  info: "bg-sky-300 text-slate-950",
  neutral: "bg-slate-500 text-slate-950",
};

const SOFT: Record<BadgeTone, string> = {
  error: "bg-red-500/20 text-red-300",
  warning: "bg-amber-500/20 text-amber-300",
  todo: "bg-violet-500/20 text-violet-300",
  info: "bg-sky-500/20 text-sky-300",
  neutral: "bg-slate-500/20 text-slate-300",
};

const DOT: Record<BadgeTone, string> = {
  error: "bg-red-400",
  warning: "bg-amber-400",
  todo: "bg-violet-300",
  info: "bg-sky-300",
  neutral: "bg-slate-500",
};

export interface BadgeProps {
  readonly tone?: BadgeTone;
  /** The number to show. `<= 0` renders nothing; `> 99` renders `99+`. */
  readonly count?: number;
  /** Render a bare dot instead of a count (magnitude unknown or noisy). */
  readonly dot?: boolean;
  readonly placement?: BadgePlacement;
  /** The surface the corner ring punches through (default `panel-secondary`). */
  readonly ringClass?: string;
  readonly className?: string;
}

export function Badge({
  tone = "neutral",
  count,
  dot = false,
  placement = "inline",
  ringClass = "ring-panel-secondary",
  className,
}: BadgeProps) {
  const corner = placement === "corner";
  if (dot) {
    return (
      <span
        aria-hidden="true"
        className={cx(
          "h-1.5 w-1.5 rounded-full",
          corner && cx("absolute right-0.5 top-0.5 ring-2", ringClass),
          DOT[tone],
          className,
        )}
      />
    );
  }
  if (count === undefined || count <= 0) return null;
  return (
    <span
      aria-hidden="true"
      className={cx(
        "flex h-[15px] min-w-[15px] items-center justify-center rounded-full px-1",
        "text-[9px] font-semibold leading-none tabular-nums",
        corner ? cx("absolute -right-1 -top-1 ring-2", ringClass, SOLID[tone]) : SOFT[tone],
        className,
      )}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}
